// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { IDisk, IPBAInfo } from './subscription';

// Update these in the back-end, too.
export const K2_NAME = 'Cigent Secure SSD';
export const K2_FIPS_NAME = 'Cigent Secure SSD FIPS';
export const ASPEN_NAME = "Cigent Secure SSD FIPS";
export const CIGENT_FIPS_NAME = "Cigent FIPS SSD";
export const DENALI_NAME = "Cigent Secure SSD+";
export const CIGENT_DM_NAME = "Cigent Secure SSD DM";
export const EVEREST_NAME = "Cigent Secure SSD Premium";
export const UNKNOWN_CIGENT_NAME = "Unknown Cigent SSD Drive";

export const SUPPORT_DUAL_MODE = 0x0000000000000008n;
export const SUPPORT_K2 = 0x0000000000004000n;
export const SUPPORT_EVEREST = 0x0000000000008000n;
export const SUPPORT_SMCU = 0x0000000100000000n;
export const SUPPORT_FIPS = 0x0000000000000040n;

export const PARTNER_UNKNOWN = 0x0100000000000000n;
export const PARTNER_CIGENT = 0x0200000000000000n;
export const PARTNER_DIGISTOR = 0x0400000000000000n;
export const PARTNER_SEAGATE = 0x0800000000000000n;
export const PARTNER_KANGURU = 0x2000000000000000n;
export const PARTNER_VRS = 0x4000000000000000n;

export const  TB = 1099511627776;
export const  GB = 1073741824;
export const  MB = 1048576;

// Opal states in disk config
export enum OPAL_STATE {
    OPAL_NOT_PROVISIONED = 0,
    OPAL_CIGENT_PROVISIONED = 1,
    OPAL_THIRD_PARTY_PROVISIONED = 2,
    OPAL_UNKNOWN = 3,
    OPAL_SUPPORTED = 4,
    OPAL_NOT_SUPPORTED = 5
}

export interface IDrive {
    serial: string;
    model: string;
    capacity: number;
    removable: number; // 0 is internal, 1 is external
    configurable: boolean; // if version supports provisioning, and it is in an unconfigured state
    configured: number; // 0 is unconfigured, 1 is configured
    connected_to: string;
    license_last_check_dttm: string; // license last check
    license_id?: number;
    license_uuid: string;
    license_version: string;
    last_dttm: string; // when drive last reported in config
    connected: number;
    last_seen: string;
    typeDisplay?: string;
    capacityDisplay?: string;
    stateDisplay?: string;
    last_seen_display?: string;
    opal_state?: number;
    disk_config_state?: number;
    opal_state_display?: string;
    pba_info?: IPBAInfo;
    pba_display?: string;
    ransomware_detection?: number; // 0: N/A (not supported); 1: Disabled; 2: Enabled
    ransomware_detection_display?: string;
    ransomwareRawSensitivity?: number;
    ransomware_sensitivity_display?: string;
    has_disk_defaults?: boolean;
    configMode?: number;  // 0: Not configured; 1: User configured; 2: Remote configured
    keep_alive:  0 | 1 | 2; // 0: N/A (not supported); 1: Disabled; 2: Enabled
    keep_alive_display: string;
    command_logging:  0 | 1 | 2; // 0: N/A (not supported); 1: Disabled; 2: Enabled
    command_logging_display: string;
    drive_partner?: string;
}

export interface IDriveResponse {
    drives: IDrive [];
    totalDrives: number;
    internalCount: number;
    externalCount: number;
    seenInLast7Days: number;
}

export interface IDiskConfigResponse {
    serial: string;
    disk_config: string;
}

export interface IVolumeConfigResponse {
    key_id: string;
    volume_config: string;
    key: string;
}

export const DASHBOARD_LAST_SEEN_CUTOFF_DAYS = 7;
export const SECONDS_PER_DAY = 24 * 60 * 60;

export interface ISecureSsdDashboardInfo {
    total: number;
    internal: number;
    external: number;
    seen: number;
}

export interface ISecureSsdDriveInfo {
    model: string;
    count: number;
    configuredCount: number;
    unconfiguredCount: number;
    unconfiguredTooltip: string;
    credsRequired: number;
    na: number;
}

function isBitSet(flags: bigint, mask: bigint): boolean {
    return (flags & mask) != 0n;
}

export function getSSDModelName(supportFlags: bigint): string {

    // Cigent Secure SSD / Cigent Secure SSD FIPS
    if (isBitSet(supportFlags, SUPPORT_K2) ||
      (isBitSet(supportFlags, SUPPORT_EVEREST) &&
        !isBitSet(supportFlags, SUPPORT_SMCU) &&
        !isBitSet(supportFlags, SUPPORT_DUAL_MODE))) {
        if (isBitSet(supportFlags, SUPPORT_FIPS)) {
            return ASPEN_NAME;
        } else {
            return K2_NAME;
        }
    }

    // Cigent FIPS SSD
    if (!isBitSet(supportFlags, SUPPORT_K2) &&
      !isBitSet(supportFlags, SUPPORT_EVEREST) &&
      isBitSet(supportFlags, SUPPORT_FIPS)) {
        return CIGENT_FIPS_NAME;
    }

    // Cigent Secure SSD+ (denali)
    if (isBitSet(supportFlags, SUPPORT_EVEREST) && isBitSet(supportFlags, SUPPORT_SMCU)) {
        return DENALI_NAME;
    }

    // Cigent Secure SSD DM
    if (isBitSet(supportFlags, SUPPORT_EVEREST) &&
      isBitSet(supportFlags, SUPPORT_DUAL_MODE) &&
      !isBitSet(supportFlags, SUPPORT_SMCU)) {
        return CIGENT_DM_NAME;
    }

    // TODO: Add logic to identify Everest SSD. Not supported by D3E yet.
    // Cigent Secure SSD Premium
    // if (?) {
    //     return EVEREST_NAME;
    // }

    return UNKNOWN_CIGENT_NAME;
}

export function toDriveInfo(disk: IDisk): IDrive {
    const drive: IDrive = new class implements IDrive {
        capacity: number;
        configurable: boolean;
        configured: number;
        connected: number;
        connected_to: string;
        last_dttm: string;
        last_seen: string;
        last_seen_display: string;
        license_last_check_dttm: string;
        model: string;
        removable: number;
        serial: string;
        stateDisplay: string;
        typeDisplay: string;
        license_uuid: string;
        license_version: string;
        keep_alive: 0 | 1 | 2;
        keep_alive_display: string;
        command_logging: 0 | 1 | 2;
        command_logging_display: string;
    };

    drive.capacity = disk.size;
    drive.serial = disk.serial;
    drive.opal_state = disk.opal_state;
    drive.disk_config_state = disk.disk_config_state;
    drive.license_version = disk.license_version;

    let support = BigInt(disk.support);
    let fips = disk.fips;
    if ( support === undefined || support == null ) {
        support = 0n;
    }
    if ( fips === undefined || fips == null ) {
        fips = 0;
    }

    // NOTE: This code sets the model for the Device Detail page.  The model is also set in the server code in
    // client_service.update_disks.
    // IF THIS CODE IS UPDATED - THE CODE IN THE SERVER MUST ALSO BE UPDATED!
    let model = null;
    if ( disk.mod === 'PCIe SSD' || disk.mod ===  'CTI SSD') {
        model = getSSDModelName(BigInt(support));
    } else {
        model = disk.mod;
    }
    drive.model = model;

    return drive;
}

export enum DiskPasswordSetting {
  AUTO_GENERATE = 0,
  USER_SUPPLIED = 1,
}

export enum DiskProtectionMode {
  SINGLE_DURING_THREATS = 0,
  SINGLE_ALWAYS = 1,
  DUAL_BOTH = 2,
}

export interface DriveTypes {
    name: string;
    size: number;
    percent: boolean;
}

export interface IProvisioningTemplate {
  id?: number;
  driveTypes: DriveTypes[];
  diskPasswordSetting: DiskPasswordSetting;
  // diskPassword: string;
  protectionMode: DiskProtectionMode;
  diskRansomware: boolean
}

export interface IProvisionDrivesRequest {
    drives: IProvisionDriveInfo[];
}

export interface IProvisionDriveInfo {
    serial: string;
    licenseId: number;
}

export enum DiskConfigMode {
    NOT_CONFIGURED = 0,
    USER_CONFIGURED = 1,
    REMOTE_CONFIGURED =2
}
